import React, { useState, useEffect, useContext } from "react";
import DashboardService from "../../../services/api/DashboardService";
import { Booking, Order, Transaction, UserStats } from "../../../Types/Dashboard";
import { PaginationType } from "../../../Types/APITypes";
import { NotifyContext } from "../../../context/notifyContext";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import Table from "../../../UI/Table/Table";
import { useMemo, useCallback } from "react";

interface UserTabsProps {
  userId: number;
}

export default function UserTabs({ userId }: UserTabsProps) {
  const [activeTab, setActiveTab] = useState<"sessions" | "orders" | "transactions" | "stats">("sessions");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [bookingsPagination, setBookingsPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [ordersPagination, setOrdersPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [transactionsPagination, setTransactionsPagination] = useState<PaginationType>({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });

  const notify = useContext(NotifyContext);
  const navigate = useNavigate();

  const fetchBookings = useCallback(
    async (paginationData?: PaginationType) => {
      const paginationToUse = paginationData || {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
      };
      setLoading(true);
      try {
        const response = await DashboardService.getUserBookings(userId, {
          page: paginationToUse.current_page,
          per_page: paginationToUse.per_page,
        });
        setBookings(response.data.bookings);
        setBookingsPagination(response.data.meta);
      } catch (err: any) {
        notify(err?.message || "Erreur lors du chargement des sessions", "error");
      } finally {
        setLoading(false);
      }
    },
    [userId, notify]
  );

  const fetchOrders = useCallback(
    async (paginationData?: PaginationType) => {
      const paginationToUse = paginationData || {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
      };
      setLoading(true);
      try {
        const response = await DashboardService.getUserOrders(userId, {
          page: paginationToUse.current_page,
          per_page: paginationToUse.per_page,
        });
        setOrders(response.data.orders);
        setOrdersPagination(response.data.meta);
      } catch (err: any) {
        notify(err?.message || "Erreur lors du chargement des commandes", "error");
      } finally {
        setLoading(false);
      }
    },
    [userId, notify]
  );

  const fetchTransactions = useCallback(
    async (paginationData?: PaginationType) => {
      const paginationToUse = paginationData || {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
      };
      setLoading(true);
      try {
        const response = await DashboardService.getUserTransactions(userId, {
          page: paginationToUse.current_page,
          per_page: paginationToUse.per_page,
        });
        setTransactions(response.data.transactions);
        setTransactionsPagination(response.data.meta);
      } catch (err: any) {
        notify(err?.message || "Erreur lors du chargement des transactions", "error");
      } finally {
        setLoading(false);
      }
    },
    [userId, notify]
  );

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await DashboardService.getUserStats(userId);
      setStats(response.data);
    } catch (err: any) {
      notify(err?.message || "Erreur lors du chargement des statistiques", "error");
    } finally {
      setLoading(false);
    }
  }, [userId, notify]);

  useEffect(() => {
    // Preload all tabs data on mount and when userId changes
    fetchBookings();
    fetchOrders();
    fetchTransactions();
    fetchStats();
  }, [userId, fetchBookings, fetchOrders, fetchTransactions, fetchStats]);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "confirmed":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const bookingsColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Date de début",
        Cell: (params: any) => {
          const { row } = params;
          return <p>{formatDate(row.original.start_time)}</p>;
        },
      },
      {
        Header: "Statut",
        Cell: (params: any) => {
          const { row } = params;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                row.original.status
              )}`}
            >
              {row.original.status}
            </span>
          );
        },
      },
      {
        Header: "Coût",
        accessor: "credits_cost",
      },
      {
        Header: "Actions",
        Cell: (params: any) => {
          const { row } = params;
          return (
            <button
              onClick={() => navigate(`/app/bookings/${row.original.id}`)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Voir
            </button>
          );
        },
      },
    ],
    [navigate]
  );

  const ordersColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Date",
        Cell: (params: any) => {
          const { row } = params;
          return <p>{formatDate(row.original.created_at)}</p>;
        },
      },
      {
        Header: "Statut",
        Cell: (params: any) => {
          const { row } = params;
          return (
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                row.original.status
              )}`}
            >
              {row.original.status}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        Cell: (params: any) => {
          const { row } = params;
          return (
            <button
              onClick={() => navigate(`/app/orders/${row.original.id}`)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Voir
            </button>
          );
        },
      },
    ],
    [navigate]
  );

  const transactionsColumns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Date",
        Cell: (params: any) => {
          const { row } = params;
          return <p>{formatDate(row.original.created_at)}</p>;
        },
      },
      {
        Header: "Montant",
        Cell: (params: any) => {
          const { row } = params;
          return <p>{row.original.amount.toLocaleString()} {row.original.currency}</p>;
        },
      },
      {
        Header: "Crédits",
        accessor: "credits",
      },
      {
        Header: "Raison",
        accessor: "reason",
      },
    ],
    []
  );

  return (
    <div className="mt-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("sessions")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "sessions"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Sessions ({bookingsPagination.total})
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "orders"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Commandes ({ordersPagination.total})
          </button>
          <button
            onClick={() => setActiveTab("transactions")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "transactions"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Transactions ({transactionsPagination.total})
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "stats"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Statistiques
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "sessions" && (
          <div className="bg-white shadow rounded overflow-hidden">
            <Table
              columns={bookingsColumns as any}
              data={bookings}
              fetchData={fetchBookings}
              loading={loading}
              pageCount={bookingsPagination.last_page || 1}
              total={bookingsPagination.total || 0}
            />
          </div>
        )}

        {activeTab === "orders" && (
          <div className="bg-white shadow rounded overflow-hidden">
            <Table
              columns={ordersColumns as any}
              data={orders}
              fetchData={fetchOrders}
              loading={loading}
              pageCount={ordersPagination.last_page || 1}
              total={ordersPagination.total || 0}
            />
          </div>
        )}

        {activeTab === "transactions" && (
          <div className="bg-white shadow rounded overflow-hidden">
            <Table
              columns={transactionsColumns as any}
              data={transactions}
              fetchData={fetchTransactions}
              loading={loading}
              pageCount={transactionsPagination.last_page || 1}
              total={transactionsPagination.total || 0}
            />
          </div>
        )}

        {activeTab === "stats" && stats && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Sessions totales</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.bookings.total}</p>
              </div>
              <div className="p-4 bg-green-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Sessions complétées</h3>
                <p className="text-2xl font-bold text-green-600">{stats.bookings.completed}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Crédits utilisés</h3>
                <p className="text-2xl font-bold text-purple-600">{stats.bookings.credits_spent}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Solde actuel</h3>
                <p className="text-2xl font-bold text-yellow-600">{stats.credits.current_balance}</p>
              </div>
              <div className="p-4 bg-indigo-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Commandes totales</h3>
                <p className="text-2xl font-bold text-indigo-600">{stats.orders.total}</p>
              </div>
              <div className="p-4 bg-pink-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Montant total commandes</h3>
                <p className="text-2xl font-bold text-pink-600">
                  ${stats.orders.total_amount.toLocaleString()} USD
                </p>
              </div>
              <div className="p-4 bg-teal-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Transactions totales</h3>
                <p className="text-2xl font-bold text-teal-600">{stats.transactions.total}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded">
                <h3 className="text-sm font-medium text-gray-600">Crédits ajoutés</h3>
                <p className="text-2xl font-bold text-orange-600">{stats.transactions.credits_added}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
